import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useStatusStore } from '@/stores/status.store';
import { updateNotificationSettings, fetchNotificationSettings } from '@/lib/api';
import { toast } from '@/stores/toast.store';
import { DND_DURATION_PRESETS, DEFAULT_WORKING_HOURS } from '@goodchat/shared/constants';

interface DndSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

export function DndSettings({ open, onOpenChange }: DndSettingsProps) {
  const settings = useStatusStore((s) => s.myNotificationSettings);
  const setSettings = useStatusStore((s) => s.setMyNotificationSettings);
  const [dndUntil, setDndUntil] = useState<string | null>(null);
  const [workingHoursEnabled, setWorkingHoursEnabled] = useState(false);
  const [workingHours, setWorkingHours] = useState<Record<string, { start: string; end: string }>>(DEFAULT_WORKING_HOURS);
  const [timezone, setTimezone] = useState('America/New_York');
  const [emailFallbackEnabled, setEmailFallbackEnabled] = useState(true);
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (settings) {
      setDndUntil(settings.dndUntil);
      setWorkingHoursEnabled(settings.workingHoursEnabled);
      if (settings.workingHours) setWorkingHours(settings.workingHours);
      setTimezone(settings.timezone);
      setEmailFallbackEnabled(settings.emailFallbackEnabled);
      setDigestEnabled(settings.digestEnabled);
    }
  }, [settings]);

  const isDndActive = dndUntil && new Date(dndUntil) > new Date();

  const handleSetDnd = async (hours: number) => {
    setIsSubmitting(true);
    try {
      const until = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
      const res = await updateNotificationSettings({ dndUntil: until });
      setDndUntil(res.settings.dndUntil);
      setSettings({
        dndUntil: res.settings.dndUntil,
        workingHoursEnabled: res.settings.workingHoursEnabled,
        workingHours: res.settings.workingHours,
        timezone: res.settings.timezone,
        emailFallbackEnabled: res.settings.emailFallbackEnabled,
        digestEnabled: res.settings.digestEnabled,
      });
    } catch (err) {
      toast.error('Failed to enable Do Not Disturb');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearDnd = async () => {
    setIsSubmitting(true);
    try {
      const res = await updateNotificationSettings({ dndUntil: null });
      setDndUntil(null);
      setSettings({
        dndUntil: res.settings.dndUntil,
        workingHoursEnabled: res.settings.workingHoursEnabled,
        workingHours: res.settings.workingHours,
        timezone: res.settings.timezone,
        emailFallbackEnabled: res.settings.emailFallbackEnabled,
        digestEnabled: res.settings.digestEnabled,
      });
    } catch (err) {
      toast.error('Failed to turn off Do Not Disturb');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveWorkingHours = async () => {
    setIsSubmitting(true);
    try {
      const res = await updateNotificationSettings({
        workingHoursEnabled,
        workingHours: workingHoursEnabled ? workingHours : undefined,
        timezone,
        emailFallbackEnabled,
        digestEnabled,
      });
      setSettings({
        dndUntil: res.settings.dndUntil,
        workingHoursEnabled: res.settings.workingHoursEnabled,
        workingHours: res.settings.workingHours,
        timezone: res.settings.timezone,
        emailFallbackEnabled: res.settings.emailFallbackEnabled,
        digestEnabled: res.settings.digestEnabled,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to save working hours');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateDaySchedule = (day: string, field: 'start' | 'end', value: string) => {
    setWorkingHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const toggleDay = (day: string) => {
    setWorkingHours((prev) => {
      if (prev[day]) {
        const { [day]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [day]: { start: '09:00', end: '17:00' } };
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900">
          <Dialog.Title className="text-lg font-semibold">Notification Settings</Dialog.Title>

          <div className="mt-4 space-y-6">
            {/* DND Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Do Not Disturb</h3>
              {isDndActive ? (
                <div className="mt-2 flex items-center gap-3">
                  <span className="text-sm text-amber-600 dark:text-amber-400">
                    DND active until {new Date(dndUntil!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <button
                    onClick={handleClearDnd}
                    disabled={isSubmitting}
                    className="rounded-lg px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-950"
                  >
                    Turn off
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  {DND_DURATION_PRESETS.map((h) => (
                    <button
                      key={h}
                      onClick={() => handleSetDnd(h)}
                      disabled={isSubmitting}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:hover:bg-gray-800"
                    >
                      {h}h
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Working Hours Section */}
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Working Hours</h3>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={workingHoursEnabled}
                    onChange={(e) => setWorkingHoursEnabled(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="h-5 w-9 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-gray-600" />
                </label>
              </div>

              {workingHoursEnabled && (
                <div className="mt-3 space-y-2">
                  <div className="mb-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400">Timezone</label>
                    <select
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className="mt-0.5 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    >
                      {Intl.supportedValuesOf('timeZone').filter((tz) => tz.startsWith('America/') || tz.startsWith('Europe/') || tz.startsWith('Asia/') || tz.startsWith('Pacific/')).map((tz) => (
                        <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </div>
                  {DAYS.map((day) => {
                    const schedule = workingHours[day];
                    return (
                      <div key={day} className="flex items-center gap-2">
                        <label className="flex w-12 items-center gap-1.5">
                          <input
                            type="checkbox"
                            checked={!!schedule}
                            onChange={() => toggleDay(day)}
                            className="rounded border-gray-300"
                          />
                          <span className="text-xs font-medium">{DAY_LABELS[day]}</span>
                        </label>
                        {schedule ? (
                          <>
                            <input
                              type="time"
                              value={schedule.start}
                              onChange={(e) => updateDaySchedule(day, 'start', e.target.value)}
                              className="rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            />
                            <span className="text-xs text-gray-400">to</span>
                            <input
                              type="time"
                              value={schedule.end}
                              onChange={(e) => updateDaySchedule(day, 'end', e.target.value)}
                              className="rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            />
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">Off</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Email Notifications Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Email Notifications</h3>
              <div className="mt-2 space-y-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={emailFallbackEnabled}
                    onChange={(e) => setEmailFallbackEnabled(e.target.checked)}
                    className="mt-0.5 rounded border-gray-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Email fallback</span>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Receive email notifications for announcements and reminders when you have no push-enabled device.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={digestEnabled}
                    onChange={(e) => setDigestEnabled(e.target.checked)}
                    className="mt-0.5 rounded border-gray-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Daily digest</span>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Receive a daily summary of unread messages across all your areas.</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800">
              Cancel
            </Dialog.Close>
            <button
              onClick={handleSaveWorkingHours}
              disabled={isSubmitting}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
